import type { InfoRichTextEditorDialog } from "../../../dialogs/info/rich-text-editor";

import sanitizeHtml from "sanitize-html";
import { twMerge } from "tailwind-merge";

import { addLinkAriaLabels } from "../../../helpers/info/rich-text-editor";

const ANCHOR_TAG_CSS = twMerge(
  `
    [&_a]:cursor-pointer [&_a]:text-foreground-primary [&_a]:focus-outline

    group-data-[has-dark-theme=true]/theme:[&_a]:text-foreground-on-primary
    group-data-[has-dark-theme=true]/theme:[&_a]:underline
    group-data-[has-dark-theme=true]/theme:[&_a]:outline-border-variant-lowest

    [&_a]:hover:text-foreground-primary-hover [&_a]:hover:underline

    group-data-[has-dark-theme=true]/theme:hover:[&_a]:text-foreground-on-primary

    [&_a[type='external']]:after:ms-1 [&_a[type='external']]:after:inline-block
    [&_a[type='external']]:after:content-['↗']

    [&_a[type='internal']]:after:ms-1 [&_a[type='internal']]:after:inline-block
    [&_a[type='internal']]:after:h-2 [&_a[type='internal']]:after:w-1
    [&_a[type='internal']]:after:bg-current
    [&_a[type='internal']]:after:align-middle
    [&_a[type='internal']]:after:content-['']
    [&_a[type='internal']]:after:[clip-path:polygon(0_0,100%_50%,0_100%)]
  `,
);
const BASE_CSS = twMerge(
  `
    text-body text-foreground-on-surface

    group-data-[has-dark-theme=true]/theme:text-foreground-on-primary
    group-data-[has-dark-theme=true]/theme:outline-border-variant-lowest

    [&_li]:text-start
  `,
);
const BLOCKQUOTE_CSS = twMerge(
  `
    relative

    [&_blockquote]:text-body [&_blockquote]:text-foreground-primary-variant

    group-data-[has-dark-theme=true]/theme:[&_blockquote]:text-foreground-on-primary

    [&_blockquote]:before:text-[4rem]
    [&_blockquote]:before:text-foreground-icon-splash-color-1

    group-data-[has-dark-theme=true]/theme:[&_blockquote]:before:text-foreground-on-primary

    [&_blockquote]:md:text-title-m [&_blockquote]:md:font-semibold

    [&_blockquote_p]:ms-8 [&_blockquote_p]:font-normal

    md:[&_blockquote_p]:leading-7!

    [&_blockquote:first-of-type::before]:absolute
    [&_blockquote:first-of-type::before]:start-0
    [&_blockquote:first-of-type::before]:top-0
    [&_blockquote:first-of-type::before]:leading-10
    [&_blockquote:first-of-type::before]:content-['“']
  `,
);
const CITATION_CSS = twMerge(
  `
    [&_blockquote_p_cite]:ms-0

    [&_cite]:ms-8 [&_cite]:mt-2 [&_cite]:block [&_cite]:text-label!
    [&_cite]:font-semibold! [&_cite]:text-foreground-on-surface-variant-medium
    [&_cite]:uppercase [&_cite]:not-italic

    group-data-[has-dark-theme=true]/theme:[&_cite]:text-foreground-on-primary

    md:[&_cite]:mt-3
  `,
);
const H3_CSS = twMerge(
  `
    [&_*+h3]:mt-4

    md:[&_*+h3]:mt-6

    [&_h3]:mb-3 [&_h3]:text-title-m! [&_h3]:font-semibold
    [&_h3]:text-foreground-primary-variant

    group-data-[has-dark-theme=true]/theme:[&_h3]:text-foreground-on-primary
  `,
);
const H4_CSS = twMerge(
  `
    [&_*+h4]:mt-3

    md:[&_*+h4]:mt-5

    [&_h4]:mb-2 [&_h4]:text-subtitle-l! [&_h4]:font-semibold
    [&_h4]:text-foreground-primary-variant

    group-data-[has-dark-theme=true]/theme:[&_h4]:text-foreground-on-primary
  `,
);
const LEGAL_TEXT_CSS = twMerge(
  `
    [&_ol>li:has(small)]:marker:text-caption

    [&_small]:text-caption
  `,
);
const ORDERED_LIST_CSS = twMerge(
  `
    [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:ps-10

    [&_ol_li]:mb-1

    [&_ol_ol]:my-1
  `,
);
const PARAGRAPH_CSS = twMerge(`
  [&_*+p]:mt-2

  md:[&_*+p]:mt-3
`);
const UNORDERED_LIST_CSS = twMerge(
  `
    [&_li]:mb-1

    [&_ul]:my-2 [&_ul]:list-[square] [&_ul]:ps-10

    [&_ul_ul]:my-1

    [&_ul_ul_ul>li]:marker:text-foreground-primary

    [&_ul_ul>li]:marker:text-foreground-icon-splash-color-4

    [&_ul>li]:marker:text-foreground-icon-splash-color-2

    group-data-[has-dark-theme=true]/theme:[&_ul>li]:marker:text-foreground-on-primary
  `,
);

export const InfoRichTextEditor = ({
  className,
  containerClassName,
  content,
}: InfoRichTextEditorDialog & {
  className?: string | undefined;
  containerClassName?: string | undefined;
}) => {
  const addedAriaLabelsContent = addLinkAriaLabels({
    html: content,
  });

  const sanitizedContent = addedAriaLabelsContent
    ? sanitizeHtml(addedAriaLabelsContent, {
        allowedAttributes: {
          "*": ["class"],
          a: ["aria-label", "href", "target", "type"],
        },
      })
    : "";

  if (!sanitizedContent) {
    return null;
  }

  const twClassName = twMerge(
    BASE_CSS,
    ANCHOR_TAG_CSS,
    BLOCKQUOTE_CSS,
    CITATION_CSS,
    H3_CSS,
    H4_CSS,
    LEGAL_TEXT_CSS,
    ORDERED_LIST_CSS,
    PARAGRAPH_CSS,
    UNORDERED_LIST_CSS,
    className,
  );

  const twContainerClassName = twMerge(
    `
      container mx-auto

      [*+&]:mt-4

      md:[*+&]:mt-6
    `,
    containerClassName,
  );

  return (
    <div className={twContainerClassName}>
      <div
        className={twClassName}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        data-fs-element="rich-text-editor"
      />
    </div>
  );
};
